import type { ReportParameter, ReportParameterValues, ReportRow } from './api'

export function parameterDefaults(fields: ReportParameter[]): ReportParameterValues {
  return Object.fromEntries(fields.map(field => [field.key, field.type === 'table' ? [] : field.default ?? (field.type === 'boolean' ? false : '')]))
}

export function validateReportParameters(fields: ReportParameter[], values: ReportParameterValues): Record<string, string> {
  const errors: Record<string, string> = {}
  for (const field of fields) {
    const value = values[field.key]
    if (field.type === 'table') {
      const rows = Array.isArray(value) ? value : []
      if (!Array.isArray(value) || rows.length < Math.max(field.min_rows || 0, Number(field.required))) {
        errors[field.key] = 'Добавьте строки в таблицу.'
      }
      if (rows.length > (field.max_rows || 30)) {
        errors[field.key] = `Не более ${field.max_rows || 30} строк.`
      }
      rows.forEach((row, index) => {
        const rowErrors = validateReportParameters(field.columns || [], row)
        Object.entries(rowErrors).forEach(([key, message]) => {
          errors[`${field.key}.${index}.${key}`] = message
        })
      })
      continue
    }
    const empty = value == null || (typeof value === 'string' && !value.trim())
    if (empty) {
      if (field.required) {
        errors[field.key] = `Заполните поле «${field.label}».`
      }
      continue
    }
    if (typeof value === 'string' && ['string', 'text'].includes(field.type) && value.length > field.max_length) {
      errors[field.key] = `Не более ${field.max_length} знаков.`
    }
    if (field.type === 'date' && (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value) || Number.isNaN(Date.parse(value)) || new Date(value).toISOString().slice(0, 10) !== value)) {
      errors[field.key] = 'Укажите дату.'
    }
    if (['employee', 'select'].includes(field.type) && !field.choices.some(choice => choice.value === value)) {
      errors[field.key] = 'Выберите значение из списка.'
    }
    if (['integer', 'number'].includes(field.type) && (!Number.isFinite(Number(value)) || (field.type === 'integer' && !Number.isInteger(Number(value))))) {
      errors[field.key] = 'Введите корректное число.'
    }
    if (['integer', 'number'].includes(field.type) && ((field.minimum != null && Number(value) < field.minimum) || (field.maximum != null && Number(value) > field.maximum))) {
      errors[field.key] = `Допустимое значение: ${field.minimum ?? '−∞'}…${field.maximum ?? '∞'}.`
    }
    if (field.must_be_true && value !== true) {
      errors[field.key] = 'Подтвердите проверку сведений.'
    }
    if (field.not_before && typeof value === 'string' && values[field.not_before] && value < String(values[field.not_before])) {
      errors[field.key] = 'Дата окончания не может быть раньше даты начала.'
    }
    if (field.different_from && value === values[field.different_from]) {
      errors[field.key] = 'Выберите другого сотрудника: роли не должны совпадать.'
    }
  }
  return errors
}

export function emptyReportRow(columns: ReportParameter[]): ReportRow {
  return parameterDefaults(columns) as ReportRow
}

// A copy gets today's document date; clinical, leave and course dates remain explicit.
export function copiedParameters(fields: ReportParameter[], saved: ReportParameterValues): ReportParameterValues {
  const result = JSON.parse(JSON.stringify(saved)) as ReportParameterValues
  fields.filter(field => field.default_from === 'today').forEach(field => {
    result[field.key] = field.default
  })
  return result
}
